import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getEnrollments, getCertificates, getCourses, getMyAssignedCertificates, claimMyCertificates } from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { BookOpen, Award, Clock, Download, Play, MessageCircle, Calendar, Target, TrendingUp, ExternalLink, Archive, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentDashboard = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [assignedCerts, setAssignedCerts] = useState({ external: [], legacy: [], total: 0 });
  const [claiming, setClaiming] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [enrollRes, certRes, coursesRes, assignedRes] = await Promise.all([
        getEnrollments({ user_id: user.id }),
        getCertificates(user.id),
        getCourses(),
        getMyAssignedCertificates().catch(() => ({ data: { external: [], legacy: [], total: 0 } })),
      ]);
      setEnrollments(enrollRes.data || []);
      setCertificates(certRes.data || []);
      setCourses(coursesRes.data || []);
      setAssignedCerts(assignedRes.data || { external: [], legacy: [], total: 0 });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const r = await claimMyCertificates();
      const { total } = r.data;
      if (total > 0) {
        toast.success(`Se reclamaron ${total} certificado(s) que coinciden con tu email`);
        fetchData();
      } else {
        toast.info('No se encontraron certificados pendientes de reclamar para tu email');
      }
    } catch {
      toast.error('Error al reclamar certificados');
    } finally { setClaiming(false); }
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const completedEnrollments = enrollments.filter(e => e.status === 'completed');
  const avgProgress = activeEnrollments.length > 0 
    ? activeEnrollments.reduce((acc, e) => acc + (e.progress || 0), 0) / activeEnrollments.length 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 bg-muted/30 min-h-screen" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
            ¡Bienvenido, {user.name}!
          </h1>
          <p className="text-muted-foreground">Continúa tu aprendizaje y alcanza tus metas</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cursos Activos</p>
                  <p className="text-2xl font-bold text-blue-700">{activeEnrollments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Award className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Completados</p>
                  <p className="text-2xl font-bold text-green-700">{completedEnrollments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Target className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Certificados</p>
                  <p className="text-2xl font-bold text-amber-700">{certificates.length + assignedCerts.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Progreso Promedio</p>
                  <p className="text-2xl font-bold text-purple-700">{avgProgress.toFixed(0)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content - Active Courses */}
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Mis Cursos en Progreso</CardTitle>
                    <CardDescription>Continúa donde lo dejaste</CardDescription>
                  </div>
                  <Link to="/courses">
                    <Button variant="outline" size="sm">Ver todos</Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {activeEnrollments.length === 0 ? (
                  <div className="text-center py-10">
                    <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No estás inscrito en ningún curso</p>
                    <Link to="/courses">
                      <Button>Explorar Cursos</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeEnrollments.slice(0, 3).map((enrollment) => (
                      <div 
                        key={enrollment.id}
                        className="p-4 border rounded-xl hover:border-accent/50 hover:shadow-sm transition-all bg-background"
                        data-testid={`enrollment-${enrollment.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground truncate">{enrollment.course_title}</h3>
                            <div className="flex items-center gap-3 mt-2">
                              <Progress value={enrollment.progress || 0} className="flex-1 h-2" />
                              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                                {(enrollment.progress || 0).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          <Link to={`/courses/${enrollment.course_id}/learn`}>
                            <Button size="sm" className="gap-2 shrink-0">
                              <Play className="w-3 h-3" />
                              Continuar
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Certificates */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-lg">Mis Certificados</CardTitle>
                    <CardDescription>
                      {certificates.length + assignedCerts.total} certificado(s) en total
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClaim}
                    disabled={claiming}
                    className="gap-1.5"
                    data-testid="claim-certs-btn"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${claiming ? 'animate-spin' : ''}`} />
                    Reclamar por email
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {certificates.length === 0 && assignedCerts.total === 0 ? (
                  <div className="text-center py-8">
                    <Award className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">Completa cursos o pulsa "Reclamar por email" para vincular certificados existentes.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="my-certificates-list">
                    {/* Course certificates (auto) */}
                    {certificates.map((cert) => (
                      <div
                        key={cert.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gradient-to-r from-amber-50/50 to-transparent"
                        data-testid={`certificate-${cert.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Award className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-sm">{cert.course_title}</h4>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted-foreground font-mono">{cert.code}</p>
                              <Badge variant="outline" className="text-[10px] h-4 px-1.5">Curso</Badge>
                            </div>
                          </div>
                        </div>
                        <a href={`${API_URL}/api/certificates/download/${cert.id}`} download target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" data-testid={`download-cert-${cert.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    ))}

                    {/* External certificates (assigned by admin) */}
                    {assignedCerts.external?.map((cert) => (
                      <div
                        key={`ext-${cert.id}`}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gradient-to-r from-blue-50/50 to-transparent"
                        data-testid={`external-cert-${cert.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <Award className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-medium text-sm truncate">{cert.course_name || 'Certificado externo'}</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-muted-foreground font-mono">{cert.code}</p>
                              <Badge variant="outline" className="text-[10px] h-4 px-1.5">Externo</Badge>
                              {cert.issue_date && <span className="text-xs text-muted-foreground">· {cert.issue_date}</span>}
                            </div>
                          </div>
                        </div>
                        <a href={`${API_URL}/api/external-certificates/${cert.id}/download`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    ))}

                    {/* Legacy certificates */}
                    {assignedCerts.legacy?.map((cert) => (
                      <div
                        key={`leg-${cert.id}`}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gradient-to-r from-purple-50/50 to-transparent"
                        data-testid={`legacy-cert-${cert.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                            <Archive className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-medium text-sm truncate">{cert.student_name}</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-muted-foreground font-mono">Folio {cert.folio}</p>
                              <Badge variant="outline" className="text-[10px] h-4 px-1.5">Legacy</Badge>
                              {cert.period && <span className="text-xs text-muted-foreground">· {cert.period}</span>}
                            </div>
                          </div>
                        </div>
                        {cert.image_url && (
                          <a href={cert.image_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/courses" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <BookOpen className="w-4 h-4" />
                    Explorar Cursos
                  </Button>
                </Link>
                <Link to="/verify" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Award className="w-4 h-4" />
                    Verificar Certificado
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2"
                  onClick={() => document.querySelector('[data-testid="ai-chat-button"]')?.click()}
                  data-testid="open-ai-chat-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat con Tutor AI
                </Button>
              </CardContent>
            </Card>

            {/* Recommended Courses */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Cursos Recomendados</CardTitle>
                <CardDescription className="text-xs">Continúa tu formación</CardDescription>
              </CardHeader>
              <CardContent>
                {courses.filter(c => !enrollments.find(e => e.course_id === c.id)).slice(0, 3).map((course) => (
                  <Link 
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="block p-3 hover:bg-muted/50 rounded-lg transition-colors mb-2"
                  >
                    <h4 className="font-medium text-sm mb-1 line-clamp-2">{course.title}</h4>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{course.hours || 0} horas</span>
                    </div>
                  </Link>
                ))}
                {courses.filter(c => !enrollments.find(e => e.course_id === c.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Estás inscrito en todos los cursos disponibles
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
